// ===================================
// KAIXO - Configuración de API
// ===================================

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

// Extrae un id plano (string) de cualquier forma en que pueda llegar:
// string id, objeto con _id/id, ObjectId con toHexString(), o el literal
// "[object Object]" (cuando algún caller pre-casteó con String(obj)).
// Se usa en TODAS las funciones v2* que construyen URLs con ids — evita
// de raíz el patrón de bug donde un objeto acaba serializado en la URL
// como "[object Object]" → 400/404 del backend.
function idOf(v) {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v === '[object Object]' ? '' : v;
    if (typeof v === 'object') {
        if (typeof v.toHexString === 'function') {
            try { return v.toHexString(); } catch { /* noop */ }
        }
        if (v._id) return idOf(v._id);
        if (v.id) return idOf(v.id);
        return '';
    }
    return String(v);
}

const api = axios.create({
    baseURL: API_URL
});

// Decodifica el payload de un JWT (sin validar firma) para leer el userId
function getTokenUserId(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId;
    } catch { return null; }
}

// Interceptor: añade el token JWT si coincide con el usuario actual
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('kaixo_token');
    const userStr = localStorage.getItem('kaixo_user');
    if (token && userStr) {
        try {
            const user = JSON.parse(userStr);
            const tokenUserId = getTokenUserId(token);
            if (tokenUserId && user._id && tokenUserId !== user._id.toString()) {
                // Token desincronizado — borrarlo
                console.warn('⚠️ Token desincronizado con usuario, eliminando');
                localStorage.removeItem('kaixo_token');
            } else {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

// Interceptor de respuesta: si el backend devuelve 401 (token expirado/inválido),
// limpiamos la sesión y forzamos relogin. Flag local para prevenir múltiples
// reloads en cascada si varias requests fallan simultáneamente.
let sessionExpiredHandled = false;

// Reset del flag cuando el usuario se loguea de nuevo (disparado por App.js tras login).
// Evita que un 401 previo deje la app sin poder detectar futuros 401.
export function resetSessionExpiredFlag() {
    sessionExpiredHandled = false;
}
// También escuchamos el evento login-success para auto-resetear
if (typeof window !== 'undefined') {
    window.addEventListener('kaixo-login-success', () => { sessionExpiredHandled = false; });
}
api.interceptors.response.use(
    (r) => r,
    (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const data = error.response?.data || {};
        const isCritical = url.includes('/messages/') || url.includes('/groups/') || url.includes('/users/');

        // 401 en rutas críticas → logout
        if (status === 401 && !sessionExpiredHandled && isCritical) {
            const hadToken = !!localStorage.getItem('kaixo_token');
            if (hadToken) {
                sessionExpiredHandled = true;
                console.warn('⚠️ 401 en ruta crítica, cerrando sesión');
                localStorage.removeItem('kaixo_token');
                localStorage.removeItem('kaixo_user');
                window.dispatchEvent(new CustomEvent('session_expired'));
                if (!window.location.pathname.startsWith('/c/')) {
                    setTimeout(() => window.location.reload(), 500);
                }
            }
        }

        // 403 / 429 por límites de plan → disparar UpgradeModal
        if ((status === 403 || status === 429) &&
            (data.error === 'plan_limit_exceeded' || data.error === 'plan_quota_exceeded')) {
            window.dispatchEvent(new CustomEvent('kaixo-upgrade-required', { detail: data }));
        }

        return Promise.reject(error);
    }
);

// Usuarios
export const createUser = (userData) => api.post('/users/test-create', userData);
export const getAllUsers = () => api.get('/users/all');
export const savePushSubscription = (userId, subscription) => api.put('/users/push-subscription', { userId, subscription });

// Mensajes
export const sendMessage = (messageData) => api.post('/messages/send', messageData);
export const getMessages = (conversationId, userId, before = null) =>
    api.get(`/messages/${conversationId}?userId=${userId}${before ? `&before=${encodeURIComponent(before)}` : ''}`);
export const getConversations = (userId) => api.get(`/messages/conversations/${userId}`);
// Resuelve si existe una conversación 1-a-1 entre el user actual y otherUserId,
// sin crearla. Devuelve { exists, conversation? }
export const findConversationWith = (otherUserId, userId) =>
    api.get(`/messages/with/${otherUserId}?userId=${userId}`);

// Mensajes de grupo
export const sendGroupMessage = (messageData) => api.post('/messages/send-group', messageData);
export const getGroupMessages = (groupId, userId, before = null) =>
    api.get(`/messages/group/${groupId}?userId=${userId}${before ? `&before=${encodeURIComponent(before)}` : ''}`);

// Grupos
export const createGroup = (groupData) => api.post('/groups/create', groupData);
export const getUserGroups = (userId) => api.get(`/groups/user/${userId}`);
export const getGroupDetails = (groupId) => api.get(`/groups/${groupId}`);
export const leaveGroup = (data) => api.post('/groups/leave', data);
export const deleteGroup = (data) => api.post('/groups/delete', data);
export const addGroupMembers = (data) => api.post('/groups/add-members', data);
export const removeGroupMember = (data) => api.post('/groups/remove-member', data);
export const promoteGroupAdmin = (data) => api.post('/groups/promote-admin', data);
export const demoteGroupAdmin = (data) => api.post('/groups/demote-admin', data);
export const blockGroupMember = (data) => api.post('/groups/block-member', data);
export const unblockGroupMember = (data) => api.post('/groups/unblock-member', data);
export const updateGroupInfo = (groupId, data) => api.put(`/groups/${groupId}/info`, data);
export const acknowledgeGroup = (groupId) => api.post(`/groups/${groupId}/acknowledge`);

// Denuncias / moderación
export const getReportReasons = () => api.get('/reports/reasons');
export const sendReport = (data) => api.post('/reports', data);

// Contactos
export const syncContacts = (data) => api.post('/contacts/sync', data);
export const getContacts = (userId) => api.get(`/contacts/${userId}`);

// Invitaciones
export const createInvitation = (data) => api.post('/invitations/create', data);
export const getPersonalInviteLink = (userId) => api.get(`/invitations/link/${userId}`);

// Multimedia
export const sendImage = (formData) => api.post('/media/image', formData);
export const sendGroupImage = (formData) => api.post('/media/group-image', formData);
export const sendVideo = (formData) => api.post('/video/send', formData);
export const sendGroupVideo = (formData) => api.post('/video/send-group', formData);
// Reacciones
export const reactToMessage = (messageId, data) => api.post(`/messages/${messageId}/react`, data);
export const deleteMessage = (messageId, data) => api.delete(`/messages/${messageId}`, { data });

// Editar mensaje de texto
export const editMessage = (messageId, data) => api.put(`/messages/${messageId}`, data);

// Búsqueda global de mensajes
export const searchMessages = (userId, query) => api.get(`/messages/search/${userId}`, { params: { q: query } });

// Audio
export const sendAudio = (formData) => api.post('/audio/send', formData);
export const sendGroupAudio = (formData) => api.post('/audio/send-group', formData);
export const forwardAudio = (data) => api.post('/audio/forward', data);

// Documentos
export const sendDocument = (formData) => api.post('/document/send', formData);
export const sendGroupDocument = (formData) => api.post('/document/send-group', formData);

export const markAsRead = (conversationId, data) => api.post(`/messages/read/${conversationId}`, data);
export const markGroupAsRead = (data) => api.post('/groups/read', data);

// Acciones de usuario
export const blockUser = (data) => api.post('/users/block', data);
export const unblockUser = (data) => api.post('/users/unblock', data);
export const muteConversation = (data) => api.post('/users/mute', data);
export const unmuteConversation = (data) => api.post('/users/unmute', data);
export const addFavorite = (data) => api.post('/users/favorite', data);
export const removeFavorite = (data) => api.post('/users/unfavorite', data);
export const getUserStatus = (userId, params) => api.get(`/users/${userId}/status`, { params });
export const clearChat = (conversationId, data) => api.delete(`/messages/clear/${conversationId}`, { data });
export const deleteConversation = (conversationId, data) => api.delete(`/messages/conversation/${conversationId}`, { data });

export default api;

export const sendEditedDocument = (data) => api.post('/document/send-edited', data);
export const forwardVideo = (data) => api.post('/video/forward', data);

// Canales multilingües
export const createChannel = (data) => api.post('/channels', data);
export const getUserChannels = (userId) => api.get(`/channels/user/${userId}`);
export const getChannel = (channelId) => api.get(`/channels/${channelId}`);
export const subscribeChannel = (data) => api.post('/channels/subscribe', data);
export const unsubscribeChannel = (channelId, data) => api.post(`/channels/${channelId}/unsubscribe`, data);
export const publishChannelMessage = (channelId, data) => api.post(`/channels/${channelId}/messages`, data);
export const publishChannelImage = (channelId, formData) => api.post(`/channels/${channelId}/messages/image`, formData);
export const getChannelMessages = (channelId, params) => api.get(`/channels/${channelId}/messages`, { params });
export const deleteChannel = (channelId, data) => api.delete(`/channels/${channelId}`, { data });
export const sendChannelInquiry = (channelId, messageId, data) => api.post(`/channels/${channelId}/messages/${messageId}/inquire`, data);
export const getPublicChannel = (code, lang) => api.get(`/channels/public/${code}`, { params: { lang } });

// ── Canales V2 (aislado, convive con V1) ──
export const v2CreateChannel = (data) => api.post('/channels/v2', data);
export const v2GetUserChannels = (userId) => api.get(`/channels/v2/user/${idOf(userId)}`);
export const v2GetChannel = (channelId) => api.get(`/channels/v2/${idOf(channelId)}`);
export const v2UpdateChannel = (channelId, data) => api.put(`/channels/v2/${idOf(channelId)}`, data);
export const v2DeleteChannel = (channelId) => api.delete(`/channels/v2/${idOf(channelId)}`);
export const v2Subscribe = (data) => api.post('/channels/v2/subscribe', data);
export const v2Unsubscribe = (channelId, data) => api.post(`/channels/v2/${idOf(channelId)}/unsubscribe`, data);
export const v2PublishText = (channelId, data) => api.post(`/channels/v2/${idOf(channelId)}/messages`, data);
export const v2PublishImage = (channelId, formData) => api.post(`/channels/v2/${idOf(channelId)}/messages/image`, formData);
// Carrusel: FormData con campo 'media' repetido hasta 10 veces (fotos + videos cortos).
// Si el FormData incluye isStory='true', el latido se marca como historia 24h.
export const v2PublishCarousel = (channelId, formData) => api.post(`/channels/v2/${idOf(channelId)}/messages/carousel`, formData);
// Historias activas de canales seguidos + propios (agrupadas por canal)
export const v2GetStories = () => api.get('/channels/v2/stories');

// Minijuego de reacción
export const reactionGetState = () => api.get('/reaction/state');
export const reactionGetLeaderboard = () => api.get('/reaction/leaderboard');
export const reactionGetLeaderboardHistory = () => api.get('/reaction/leaderboard/history');
export const reactionPlay = (deltaMs) => api.post('/reaction/play', { deltaMs });

export const v2GetMessages = (channelId, params) => api.get(`/channels/v2/${idOf(channelId)}/messages`, { params });
export const v2GetFeed = (params) => api.get('/channels/v2/feed', { params });
export const v2GetByHashtag = (tag, params) => api.get(`/channels/v2/hashtag/${encodeURIComponent(tag)}`, { params });
export const v2SearchChannels = (q) => api.get('/channels/v2/search', { params: { q } });
export const v2IncrementShare = (messageId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/messages/${idOf(messageId)}/share`, { userId: uid });
};
export const v2GetMessageById = (messageId) => api.get(`/channels/v2/messages/${idOf(messageId)}`);
export const v2UpdateMessage = (messageId, data) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.put(`/channels/v2/messages/${idOf(messageId)}`, { userId: uid, ...data });
};
export const v2DeleteMessage = (messageId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.delete(`/channels/v2/messages/${idOf(messageId)}`, { params: { userId: uid } });
};
export const v2SendInquiry = (channelId, messageId, data) => {
    const cid = idOf(channelId);
    const mid = idOf(messageId);
    if (!cid || !mid) {
        return Promise.reject(new Error('Datos del anuncio incompletos (id inválido)'));
    }
    return api.post(`/channels/v2/${cid}/messages/${mid}/inquire`, data);
};
export const v2GetPublicProfile = (code, lang) => api.get(`/channels/v2/public/${encodeURIComponent(code)}`, { params: { lang } });
export const v2UploadAvatar = (formData) => api.post('/channels/v2/upload-avatar', formData);
export const v2ReactToMessage = (messageId, data) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/messages/${idOf(messageId)}/react`, { userId: uid, ...data });
};
export const v2AddComment = (messageId, data) => {
    // Fallback: incluir userId del localStorage si el JWT no llega (APK legacy)
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/messages/${idOf(messageId)}/comments`, { userId: uid, ...data });
};
export const v2DeleteComment = (commentId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.delete(`/channels/v2/comments/${idOf(commentId)}`, { params: { userId: uid } });
};
export const v2GetComments = (messageId, params) => api.get(`/channels/v2/messages/${idOf(messageId)}/comments`, { params });
export const v2TranslateComment = (commentId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/comments/${idOf(commentId)}/translate`, { userId: uid });
};
export const v2LikeComment = (commentId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/comments/${idOf(commentId)}/like`, { userId: uid });
};
// Lista de usuarios que siguen alguno de mis canales
export const v2GetMyFollowers = () => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.get('/channels/v2/my-followers', { params: { userId: uid } });
};
// Candidatos a invitar a seguir un canal (solo el dueño)
export const v2GetInviteCandidates = (channelId) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.get(`/channels/v2/${idOf(channelId)}/invite-candidates`, { params: { userId: uid } });
};
// Enviar invitación a N usuarios
export const v2InviteToFollow = (channelId, userIds) => {
    let uid = '';
    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || ''; } catch {}
    return api.post(`/channels/v2/${idOf(channelId)}/invite`, { userId: uid, userIds });
};